import { Table } from 'antd'

import { createCrudResourceFeature } from '@/Core/Features/UI/CrudResource'
import { AccessDenied } from '@/Ship/Components/AccessDenied'
import { FavoriteButton } from '@/Ship/Components/Favorites'
import { CrudResourceTemplate } from '@/Ship/Templates/CrudResource/CrudResourceTemplate'

import { CheckGlobalResourceAccessTask } from '../Tasks/CheckGlobalResourceAccessTask'
import { CheckResourcePageAccessTask } from '../Tasks/CheckResourcePageAccessTask'
import { CheckResourceCreateFormTask } from '../Tasks/CheckResourceCreateFormTask'
import { CheckResourceCreatedFromParentTask } from '../Tasks/CheckResourceCreatedFromParentTask'
import { EditInDrawerFromPageTask } from '../Tasks/EditInDrawerFromPageTask'

const CrudResourceFeature = createCrudResourceFeature()

/**
 * Конфігурація CRUD ресурсів
 * Використовуються стандартні formButtons та delete action з Core які працюють з accessFn
 */
export const CrudResourceConfig = {
    features: {
        CrudResourceFeature: {
            ...CrudResourceFeature,
            eventHandlers: {
                ...CrudResourceFeature.eventHandlers,
                // «Редагувати» в картці з прямої адреси — шухлядою: ядро вело на
                // `/…/{uuid}/edit`, де форми немає (HANDOFF, вада ядра; S9a)
                onAfterInit: { ...CrudResourceFeature.eventHandlers.onAfterInit, EditInDrawerFromPageTask },
                // Зірочка «в обране» — у шапці кожної картки з переліку
                // `FAVORITE_RESOURCES`; ядрові кнопки лишаються як були
                onButtons: { ...CrudResourceFeature.eventHandlers.onButtons, FavoriteButton },
            },
        },
    },
    table: {
        template: (tableProps: any) => {
            return (
                <Table
                    className="wl-table"
                    scroll={{
                        x: 'max-content',
                        ...(tableProps.scroll || {}),
                    }}
                    {...tableProps}
                />
            )
        },
    },
    settings: {
        tableActionsColumnPosition: 'end',
    },
    template: CrudResourceTemplate,
    // Відмова в доступі до сторінки ресурсу малюється всередині каркаса
    templateError403: AccessDenied,
    /**
     * Функція перевірки доступу до CRUD операцій
     * Використовує CheckGlobalResourceAccessTask для перевірки permissions
     */
    accessFn: ({ crudContext, accessMode, accessItem }: any) => {
        // Без режиму ядро питає, чи малювати сам контекст: для списку — чи
        // відкривати сторінку ресурсу (`pageConfig.accessFn`, довідники)
        if (!accessMode) return CheckResourcePageAccessTask(crudContext)

        switch (accessMode) {
            case 'create':
                // Право `create` на беку не завжди означає «заповни форму»:
                // знімок за період ним же й формують. Ресурсу, який формою не
                // створюють, кнопка «Додати» відкривала б картку з прочерками
                // й кнопкою «Зберегти», що нічого не збереже
                if (!CheckResourceCreateFormTask(crudContext?.resource)) return false

                // …а форма буває й повноцінною, але недостатньою: дочірній запис
                // без батька бек відхилить, тож заводять його з картки батька,
                // а реєстр лише показує.
                //
                // ⚠️ Гасимо лише вхід — ядрову «Додати», яка питає без запису.
                // Ядрова «Зберегти» питає те саме право **з** записом форми
                // (`accessItem`), і для картки, відкритої з батька, вона мусить
                // лишитися: інакше форму створення не було б чим зберегти
                if (!accessItem && !CheckResourceCreatedFromParentTask(crudContext?.resource?.key)) return false

                return CheckGlobalResourceAccessTask(crudContext?.resource?.key || '', accessMode)

            case 'read_collection':
                return CheckGlobalResourceAccessTask(crudContext?.resource?.key || '', accessMode)

            case 'read':
            case 'update':
            case 'delete':
                // Перевіряємо item-level permissions якщо вони є
                if (accessItem?._permissions?.[accessMode] === true) return true
                return false

            case 'update_status':
                // Окреме право картки, якщо бек його дає (співробітник: супер
                // адміністратор, і не на собі — `SPEC-005` §3.2). Ролями фронт
                // не перевіряє (`SPEC-003` §1.3): «сирі» ролі з входу не знають
                // ні правила «не на собі», ні свіжо змінених прав
                if (accessItem?._permissions && 'update_status' in accessItem._permissions) {
                    return accessItem._permissions['update_status'] === true
                }

                return accessItem?._permissions?.['update'] === true

            default:
                console.warn(`[CrudResourceConfig][accessFn] Unknown access mode: ${accessMode}`)
                return true
        }
    },
}
